import { useEffect, useRef, useState } from 'react';

type KeyboardHeightResult = {
  effectiveHeight: number;
  previousHeightRef: React.MutableRefObject<number>;
  keyboardSource: 'native' | 'visualViewport' | 'none';
  isSystemResizing: boolean;
};

/**
 * Custom hook to detect keyboard height from multiple sources
 * and identify if the system (Android) is handling the resize
 *
 * Handles both React Native (via native bridge) and web (via visualViewport API)
 * Also detects when Android natively resizes the window due to keyboard
 */
export const useKeyboardHeight = (
  isReactNative: boolean,
  externalKeyboardHeight?: number
): KeyboardHeightResult => {
  const [visualViewportHeight, setVisualViewportHeight] = useState(0);
  const [isSystemResizing, setIsSystemResizing] = useState(false);
  const previousHeightRef = useRef(0);
  const baselineWindowHeightRef = useRef<number | null>(null);

  // Listen to visualViewport resize events (web fallback)
  useEffect(() => {
    if (!isReactNative || !window.visualViewport) return;

    const viewport = window.visualViewport;
    const initialHeight = viewport.height;

    const handleResize = () => {
      const currentHeight = viewport.height;
      const heightDifference = Math.max(0, initialHeight - currentHeight);

      if (heightDifference !== visualViewportHeight) {
        setVisualViewportHeight(heightDifference);
      }
    };

    viewport.addEventListener('resize', handleResize);

    return () => {
      viewport.removeEventListener('resize', handleResize);
    };
  }, [isReactNative, visualViewportHeight]);

  // Detect if Android system is resizing window due to keyboard
  useEffect(() => {
    if (!isReactNative) return;

    const handleResize = () => {
      const windowInnerHeight = typeof window !== 'undefined' ? window.innerHeight : null;

      // Track baseline height on first measurement
      if (baselineWindowHeightRef.current === null && windowInnerHeight !== null) {
        baselineWindowHeightRef.current = windowInnerHeight;
        return;
      }

      // Calculate how much the window was resized
      const baseline = baselineWindowHeightRef.current;
      const resizedAmount =
        baseline !== null && windowInnerHeight !== null ? Math.max(0, baseline - windowInnerHeight) : 0;

      // System is resizing if:
      // 1. Window height changed (resizedAmount > 0)
      // 2. AND native keyboard is showing (externalKeyboardHeight > 0)
      // This means Android is handling the resize, not just visualViewport change
      const nativeHeight = externalKeyboardHeight ?? 0;
      const nextIsSystemResizing = resizedAmount > 0 && nativeHeight > 0;

      setIsSystemResizing(nextIsSystemResizing);
    };

    window.addEventListener('resize', handleResize);
    // Call once on mount to establish baseline
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isReactNative, externalKeyboardHeight]);

  // Determine effective keyboard height (prefer native over visualViewport)
  const nativeHeight = externalKeyboardHeight ?? 0;
  const effectiveHeight = nativeHeight > 0 ? nativeHeight : visualViewportHeight;

  // Determine source
  const keyboardSource: 'native' | 'visualViewport' | 'none' =
    nativeHeight > 0 ? 'native' : visualViewportHeight > 0 ? 'visualViewport' : 'none';

  return {
    effectiveHeight,
    previousHeightRef,
    keyboardSource,
    isSystemResizing
  };
};
